import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
const integrations = [{
  name: 'Odoo',
  logo: 'https://cdn.magicpatterns.com/uploads/aBgzw5faH7jNSPg2SP8a4D/Screenshot_2026-06-12_at_5.37.06_PM.png'
}, {
  name: 'WooCommerce',
  logo: 'https://cdn.magicpatterns.com/uploads/hAwQ3HbzS1qtjiBRtc1hL3/Screenshot_2026-06-12_at_5.37.29_PM.png'
}, {
  name: 'Microsoft Dynamics',
  logo: 'https://cdn.magicpatterns.com/uploads/1youVjT3WnibLysXAj1KQr/dynamics.png'
}, {
  name: 'Onshape',
  logo: 'https://cdn.magicpatterns.com/uploads/5kSabdwC9LQuf8CgBUvHdQ/Screenshot_2026-06-12_at_5.40.08_PM.png'
}, {
  name: 'QuickBooks',
  logo: 'https://cdn.magicpatterns.com/uploads/5j7T8Yj1t5rKDZBMAaxv1k/Screenshot_2026-06-12_at_5.35.18_PM.png'
}, {
  name: 'Acumatica',
  logo: 'https://cdn.magicpatterns.com/uploads/pcwuts2gum3omxqiuye9Lb/Screenshot_2026-06-12_at_5.40.39_PM.png'
}, {
  name: 'Epicor',
  logo: 'https://cdn.magicpatterns.com/uploads/437pF2oUvSRzPU4q1q7tA2/epicor.png'
}, {
  name: 'NetSuite',
  logo: 'https://cdn.magicpatterns.com/uploads/3NobZbn61Nz2usBJYJA6gq/netsuite.jpg'
}, {
  name: 'Infor',
  logo: 'https://cdn.magicpatterns.com/uploads/i6TwC5qJLVY6i9We6FbDv5/png-transparent-infor-hd-logo-thumbnail.png'
}, {
  name: 'Salesforce',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg'
}, {
  name: 'HubSpot',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3f/HubSpot_Logo.svg'
}, {
  name: 'Shopify',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg'
}, {
  name: 'Slack',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg'
}, {
  name: 'Stripe',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg'
}, {
  name: 'Xero',
  logo: 'https://cdn.magicpatterns.com/uploads/rKBUHBWxLj4udXhqVMAzGD/Screenshot_2026-06-12_at_5.38.38_PM.png'
}];
function IntegrationLogo({
  name,
  logo
}: {
  name: string;
  logo: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className="w-full h-full flex items-center justify-center rounded-lg bg-navy text-violet-electric font-bold text-lg" aria-label={name} data-id="element-912">
        {name.charAt(0)}
      </div>;
  }
  return <img src={logo} alt={name} className="w-full h-full object-contain" onError={() => setFailed(true)} data-id="element-913" />;
}
function IntegrationsGrid() {
  return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-914">
      {integrations.map((item, i) => <motion.div key={item.name} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4,
      delay: i % 8 * 0.05
    }} className="flex items-center gap-4 p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all" data-id="element-915">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center p-2 bg-white rounded-xl shadow-sm" data-id="element-916">
            <IntegrationLogo name={item.name} logo={item.logo} data-id="element-917" />
          </div>
          <span className="text-white/90 font-medium text-sm" data-id="element-918">{item.name}</span>
        </motion.div>)}
    </div>;
}
const faqs = [{
  question: 'What integrations does Thruline support?',
  answer: 'Thruline integrates with major platforms across CRM, accounting, e-commerce, messaging, and more — including QuickBooks, Xero, Salesforce, HubSpot, Shopify, Slack, Stripe, and Microsoft Dynamics. We also support custom integrations via API and webhooks.'
}, {
  question: 'Can you integrate with our proprietary or legacy systems?',
  answer: 'Yes. For systems without standard APIs, we build custom connectors during implementation. File-based integrations, database links, and webhook-driven event processing are all supported. Integration scope is defined during the free discovery phase.'
}, {
  question: 'How long does it take to set up an integration?',
  answer: 'Most pre-built integrations are configured during your implementation project and go live with the rest of your system. Custom integrations are scoped during discovery and included in your fixed-price project — no surprise costs.'
}, {
  question: 'Is data synced in real time?',
  answer: 'Yes. Integrations are bidirectional where needed — data flows in and out of Thruline automatically, eliminating duplicate entry and keeping all systems in sync. You can also configure batch syncs for specific use cases.'
}, {
  question: "What if we need an integration that isn't listed?",
  answer: 'If a system has an API, we can connect to it. During discovery, we assess your integration requirements and include any custom connectors in the project scope. Our team has built integrations with hundreds of systems across manufacturing and operations.'
}];
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-white/[0.06] last:border-b-0" data-id="element-919">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-6 text-left group" data-id="element-920">
        <span className="text-white font-semibold text-base pr-8 group-hover:text-violet-electric transition-colors" data-id="element-921">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-white/40 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-922" />
      </button>
      {open && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="pb-6" data-id="element-923">
          <p className="text-white/50 text-sm leading-relaxed" data-id="element-924">{answer}</p>
        </motion.div>}
    </div>;
}
export function IntegrationsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-violet-electric/30 pt-32 pb-20" data-id="element-925">
      <div className="max-w-7xl mx-auto px-6" data-id="element-926">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-927">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-violet-electric text-sm font-semibold tracking-widest uppercase mb-6 block" data-id="element-928">
            Connected Ecosystem
          </motion.span>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8" data-id="element-929">
            The data foundation that powers unified operations
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/60 leading-relaxed" data-id="element-930">
            Thruline integrates with all your systems and helps you fully
            customize and control your operations—so your software adapts to
            your strategy, not the other way around.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="mt-10" data-id="element-931">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 hover:bg-white/5 transition-all" data-id="element-932">
              Integrate now
              <ArrowRightIcon className="w-4 h-4" data-id="element-933" />
            </a>
          </motion.div>
        </div>

        {/* Grid Section */}
        <div className="mt-16" data-id="element-934">
          <IntegrationsGrid data-id="element-935" />
        </div>

        {/* FAQ Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mx-auto mt-32" data-id="element-936">
          <div className="text-center mb-12" data-id="element-937">
            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-938">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-939">
              Common questions about integrations
            </h2>
          </div>

          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-8" data-id="element-940">
            {faqs.map(faq => <FAQItem key={faq.question} question={faq.question} answer={faq.answer} data-id="element-941" />)}
          </div>
        </motion.section>
      </div>
    </div>;
}